import { Box, GridLegacy as Grid, Stack, Typography } from '@mui/material'

import { FeatureFlagIdJml, FeatureFlagIdRoleMiningSimilarityUx } from 'c1gen/features'
import { C1Button } from 'c1ui'
import { CopilotIcon } from 'components/core/icons/copilot'

import { PluralFacetFilter } from 'components/core/search-filter/facet'
import { LiveQueryFilter } from 'components/core/search-filter/query'
import { useFeatureChecker } from 'components/no-ui/capability'
import { useCatalog } from 'components/pages/requestcatalog/details/context/CatalogContext'
import { MetaFields } from 'lib/search'
import {
    AppEntitlementSearchServiceSearchRequest,
    AppEntitlementSearchServiceSearchTermsRequest,
    AppEntitlementSearchServiceSearchTermsRequest_TermType as TermType,
    SearchAppEntitlementCorrelationForAccessProfileRequest,
} from 'pbts/c1/api/app/v1/app_entitlement'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { appEntitlementSearchTermsAPI } from 'redux_toolkit/entitlements'

type FilterKeys = keyof Omit<SearchAppEntitlementCorrelationForAccessProfileRequest, MetaFields>
type FilterUpdate = Partial<Pick<SearchAppEntitlementCorrelationForAccessProfileRequest, FilterKeys>>
export type AppEntitlementRoleMiningFilters = 'query' | 'app' | 'resourceType'
export type FilterOnChange = (updated: FilterUpdate) => void
type Props = {
    initialState: SearchAppEntitlementCorrelationForAccessProfileRequest
    onChange: (filter: SearchAppEntitlementCorrelationForAccessProfileRequest) => void
    hiddenFilters?: AppEntitlementRoleMiningFilters[]
}

export const AppEntitlementRoleMiningSearchFilter: React.FC<Props> = ({
    onChange: _onChange,
    initialState,
    hiddenFilters = [],
}) => {
    const createInitialFilter = (): SearchAppEntitlementCorrelationForAccessProfileRequest => {
        const base = SearchAppEntitlementCorrelationForAccessProfileRequest.create(
            initialState ? { ...initialState } : {},
        )
        if (!base.recommendation?.oneofKind) {
            base.recommendation = { oneofKind: 'all', all: true }
        }
        return base
    }
    const initialFilter = useRef(createInitialFilter()).current
    const [filter, setFilter] = useState(() =>
        SearchAppEntitlementCorrelationForAccessProfileRequest.create(initialFilter))

    const hasRoleMiningFF = useFeatureChecker(FeatureFlagIdRoleMiningSimilarityUx)
    const hasJmlFF = useFeatureChecker(FeatureFlagIdJml)
    const { memberCount } = useCatalog()

    const onChange: FilterOnChange = useCallback(
        (update): void => {
            let newFilter
            let updated = false
            setFilter((old) => {
                newFilter = SearchAppEntitlementCorrelationForAccessProfileRequest.clone(old)
                SearchAppEntitlementCorrelationForAccessProfileRequest.mergePartial(newFilter, update)
                if (SearchAppEntitlementCorrelationForAccessProfileRequest.equals(newFilter, old)) {
                    return old
                }
                updated = true
                return newFilter
            })
            // react won't call setFilters recursively it seems...
            if (!newFilter || !updated) {
                return
            }
            _onChange(newFilter)
        },
        [setFilter, _onChange],
    )
    const onQueryChange = useCallback((query: string): void => onChange({ query }), [onChange])
    const clear = useCallback((): void => {
        onChange(initialFilter)
    }, [initialFilter, onChange])

    const canClear = useMemo(
        (): boolean => !SearchAppEntitlementCorrelationForAccessProfileRequest.equals(filter, initialFilter),
        [filter, initialFilter],
    )

    // NOTE: the hook we use is *more general* than the request we use to populate the list
    // SearchAppEntitlementCorrelationForAccessProfileRequest -> AppEntitlementSearchServiceSearchRequest
    const correctedType = useMemo(() => AppEntitlementSearchServiceSearchRequest.create(filter), [filter])

    // Determine which button is currently active
    const isAllEntitlementsActive = filter.recommendation?.oneofKind === 'all'
    const isAdditionsActive = filter.recommendation?.oneofKind === 'additions'

    // Handle button toggle functionality
    const handleAllEntitlementsClick = useCallback((): void => {
        if (isAllEntitlementsActive) {
            // Clear the recommendation filter if already active
            onChange({ recommendation: undefined })
        } else {
            // Set to 'all' if not active
            onChange({ recommendation: { oneofKind: 'all', all: true } })
        }
    }, [isAllEntitlementsActive, onChange])

    const handleAdditionsClick = useCallback((): void => {
        if (isAdditionsActive) {
            // Clear the recommendation filter if already active
            onChange({ recommendation: undefined })
        } else {
            // Set to 'additions' if not active
            onChange({ recommendation: { oneofKind: 'additions', additions: true } })
        }
    }, [isAdditionsActive, onChange])

    const intl = useIntl()
    return <Stack spacing={3}>
        <Stack direction="row" spacing={2}>
            {!hiddenFilters.includes('query') && <Grid item>
                <LiveQueryFilter onChange={onQueryChange} query={filter.query} />
            </Grid>}
            {!hiddenFilters.includes('app') && (
                <Grid item>
                    <PluralFacetFilter<AppEntitlementSearchServiceSearchTermsRequest, 'appIds'>
                        widePopper
                        onChange={onChange}
                        filter={correctedType}
                        term={TermType.APPS}
                        filterKey="appIds"
                        label={intl.formatMessage({ defaultMessage: 'Apps', id: 'apps' })}
                        hook={appEntitlementSearchTermsAPI.useLazyGetSearchTerms}
                        showCount={false}
                    />
                </Grid>
            )}
            {!hiddenFilters.includes('resourceType') && <Grid item>
                <PluralFacetFilter<AppEntitlementSearchServiceSearchTermsRequest, 'resourceTypeIds'>
                    onChange={onChange}
                    filter={correctedType}
                    term={TermType.RESOURCE_TYPE}
                    filterKey="resourceTypeIds"
                    label={intl.formatMessage({ defaultMessage: 'Resource type', id: 'Resource type' })}
                    hook={appEntitlementSearchTermsAPI.useLazyGetSearchTerms}
                    showCount={false}
                />
            </Grid>}

            {canClear && (
                <Grid key="clear" item>
                    <C1Button onClick={clear} variant="text" color="info">
                        <FormattedMessage id="components_entitlement_search_index_clear" defaultMessage="Clear" />
                    </C1Button>
                </Grid>
            )}
        </Stack>
        {hasRoleMiningFF && hasJmlFF && (!!memberCount && memberCount > 0) && <Stack direction="row" spacing={2}>
            <C1Button
                variant="outlined"
                onClick={handleAllEntitlementsClick}
                color={isAllEntitlementsActive ? 'primary' : 'info'}
                sx={{ backgroundColor: isAllEntitlementsActive ? 'primary.extraLight' : 'background.default' }}
            >
                <FormattedMessage id="components_entitlement_search_index_all_entitlements" defaultMessage="All entitlements" />
            </C1Button>
            <Box sx={{ width: '1px', height: 24, backgroundColor: 'divider' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CopilotIcon />
                <Typography variant="body2">
                    <FormattedMessage id="components_entitlement_search_index_role_mining" defaultMessage="Role mining:" />
                </Typography>
            </Box>
            <C1Button
                variant="outlined"
                color={isAdditionsActive ? 'primary' : 'info'}
                sx={{ backgroundColor: isAdditionsActive ? 'primary.extraLight' : 'background.default' }}
                onClick={handleAdditionsClick}
            >
                <FormattedMessage id="components_entitlement_search_index_additions" defaultMessage="Additions" />
            </C1Button>
        </Stack>}
    </Stack>
}
